import { createSelector } from "@reduxjs/toolkit";
import {
  selectSpecifiedArticlesValidationColorItems,
  selectStandardRoomValidationColorItems,
  selectValidationDecisionColorItems,
} from "../../Room/rfpValidationSelectors";
import { RootState } from "@reduxjs/toolkit/query/react";
import { IRootState } from "../../app/store";
import { ROOM_COLORING_IDS } from "../../RoomColoring";
import { sortArrayByStrings } from "../../globalUtils";
import { selectHttpSingleRoomFull } from "../../Room/httpRoomSelectors";

export type IRfpBarRowItem = {
  discipline: string; // "E" | "V" | ...
  validationId?: string; // "standardRoom.E"
  decisionId?: string | ROOM_COLORING_IDS; // "decision:standardRoom.E"
};

export type IRfpBarSpecifiedRowItem = {
  id: string; // "specifiedArticles.overall" | "specifiedArticles.<resp>"
  title: string;
};

const getDisciplineFromStandardRoomKey = (key: string): string | null => {
  const idx = key.indexOf(".");
  if (idx === -1 || idx + 1 >= key.length) return null;
  return key.slice(idx + 1);
};

const getDisciplineFromDecisionFilterId = (id: string): string | null => {
  const prefixIdx = id.indexOf(":");
  const withoutPrefix = prefixIdx >= 0 ? id.slice(prefixIdx + 1) : id;
  const idx = withoutPrefix.indexOf(".");
  if (idx === -1 || idx + 1 >= withoutPrefix.length) return null;
  return withoutPrefix.slice(idx + 1);
};

/**
 * Standardrom (poeng) + beslutning per disiplin, ferdig joinet og sortert.
 */
export const selectRfpStandardRoomRows = createSelector(
  [selectStandardRoomValidationColorItems, selectValidationDecisionColorItems],
  (standardRoomValidations, decisionFilters): IRfpBarRowItem[] => {
    const map = new Map<string, IRfpBarRowItem>();

    for (const v of standardRoomValidations) {
      const discipline = getDisciplineFromStandardRoomKey(v.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.validationId = v.id;
      map.set(discipline, existing);
    }

    for (const d of decisionFilters) {
      const discipline = getDisciplineFromDecisionFilterId(d.id);
      if (!discipline) continue;
      const existing = map.get(discipline) ?? { discipline };
      existing.decisionId = d.id;
      map.set(discipline, existing);
    }

    return Array.from(map.values()).sort((a, b) => a.discipline.localeCompare(b.discipline));
  }
);

/**
 * SpecifiedArticles dropdown-items (id + title).
 */
export const selectRfpSpecifiedRows = createSelector(
  [selectSpecifiedArticlesValidationColorItems],
  (specifiedArticles): IRfpBarSpecifiedRowItem[] => {
    return (specifiedArticles ?? []).map((i) => ({
      id: i.id,
      title: i.title,
    }));
  }
);

export const selectActiveRoomColoringId = (state: IRootState) => state.design.activeRoomColoringId;

export const selectActiveSpecifiedItem = createSelector(
  [selectRfpSpecifiedRows, selectActiveRoomColoringId],
  (specifiedRows, activeRoomColoringId) => {
    return specifiedRows.find((r) => r.id === activeRoomColoringId) ?? null;
  }
);

/**
 * Default active item for toolbar when none is selected:
 * first row -> prefer decisionId, otherwise validationId.
 */
export const selectRfpDefaultActiveRoomColoringId = createSelector(
  [selectRfpStandardRoomRows],
  (rows): string | undefined => {
    const first = rows[0];
    return first?.decisionId ?? first?.validationId;
  }
);

export const selectSpecifiedArticlesValidation = createSelector(
  [selectHttpSingleRoomFull],
  (room) => room?.validation?.specifiedArticles ?? null
);

export const selectSpecifiedArticlesResponsibilities = createSelector(
  [selectSpecifiedArticlesValidation],
  (specified): string[] => {
    const keys = Object.keys(specified?.perResponsibility ?? {});
    keys.sort((a, b) => a.localeCompare(b));
    return keys;
  }
);

export const selectRfpSpecifiedArticlesResponsibility = (state: IRootState) =>
  state.design.rfpSpecifiedArticlesResponsibility;

export const selectSpecifiedArticlesActiveSnapshot = createSelector(
  [selectSpecifiedArticlesValidation, selectRfpSpecifiedArticlesResponsibility],
  (specified, activeResp) => {
    if (!specified) return null;

    if (activeResp && specified.perResponsibility?.[activeResp]) {
      return specified.perResponsibility[activeResp];
    }

    return specified; // top-level snapshot (samlet)
  }
);

export const selectSpecifiedArticlesDiffItems = createSelector(
  [selectSpecifiedArticlesActiveSnapshot],
  (snapshot) => sortArrayByStrings(snapshot?.comparison ?? [], ["compCode"])
);

export const selectSpecifiedArticlesMatchRatePct = createSelector(
  [selectSpecifiedArticlesActiveSnapshot],
  (snapshot) => snapshot?.summary?.matchRatePct ?? null
);

export const selectSpecifiedArticlesValidatedAt = createSelector(
  [selectSpecifiedArticlesValidation],
  (specified) => specified?.validatedAt ?? null
);

export const selectShouldShowSpecifiedArticlesSegmented = createSelector(
  [selectSpecifiedArticlesResponsibilities],
  (responsibilities) => responsibilities.length > 0
);
